/**
 * ClaimPage — magic-link landing for business owners.
 *
 * Handles both restaurant and accommodation (hotel/motel/B&B) claims.
 * Copy is switched based on the listing's `niche` field.
 *
 * Flow: validate token → show listing details → collect owner email/name →
 * flip listing to claimed → for hotels this ALSO flips `template_status`
 * to `owner_claimed` and kicks off AI enrichment on the backend.
 *
 * Verification signals shown live in the UI:
 *   - Email format (RFC-5322-lite regex)
 *   - Domain match against the listing's `website` field. When the
 *     claimant's email domain matches the property's public website,
 *     we show a green tick; when they diverge, we show an amber notice
 *     but let them proceed (an operator can review flagged claims).
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import "./recipe.css";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Extract the registrable domain from a URL or bare hostname. */
function extractDomain(raw) {
  if (!raw) return "";
  let host = String(raw).trim().toLowerCase();
  // strip protocol + path
  host = host.replace(/^https?:\/\//, "").replace(/^www\./, "").split(/[/?#]/, 1)[0];
  // strip trailing dot / port
  host = host.replace(/:\d+$/, "").replace(/\.$/, "");
  return host;
}

/** Split an email into [local, domain]. Returns ["",""] on invalid. */
function splitEmail(email) {
  if (!email || typeof email !== "string") return ["", ""];
  const at = email.lastIndexOf("@");
  return at > 0 ? [email.slice(0, at).toLowerCase(), email.slice(at + 1).toLowerCase()] : ["", ""];
}

/** True when the two hostnames share the same registrable domain
 *  (naive apex compare — good enough for consumer email vs a domain). */
function domainMatches(emailDomain, siteDomain) {
  if (!emailDomain || !siteDomain) return false;
  if (emailDomain === siteDomain) return true;
  // strip common subdomain (bookings.foo.com.au vs mail.foo.com.au)
  const eParts = emailDomain.split(".").slice(-3).join(".");
  const sParts = siteDomain.split(".").slice(-3).join(".");
  return eParts === sParts;
}

/**
 * Build category-aware copy for the claim page.
 *
 * @param {object} restaurant  Listing document from /api/claim/{token}.
 * @returns {object}           { kind, eyebrow, title_prefix, body, benefits,
 *                               success_benefits, newsletter_label }
 */
function buildCopy(restaurant) {
  const niche = (restaurant?.niche || "").toLowerCase();
  const listing_type = (restaurant?.listing_type || "").toLowerCase();
  const sub = (restaurant?.sub_niche || "").toLowerCase();
  const isAccom = niche === "accommodation"
                    || listing_type === "accommodation"
                    || /hotel|motel|hostel|resort|b&b|bnb|guesthouse|caravan|camp|lodge|inn/i.test(sub);

  if (isAccom) {
    const kind = sub.includes("motel") ? "motel"
                    : sub.includes("caravan") ? "park"
                    : sub.includes("hostel") || sub.includes("backpacker") ? "hostel"
                    : sub.includes("b&b") || sub.includes("bnb") ? "B&B"
                    : "property";
    return {
      kind,
      eyebrow: "Claim your property",
      submitLabel: `Claim my ${kind} →`,
      body: (
        <>
          You&apos;ve been listed on HustleDirectory — the AU-wide accommodation
          index that&apos;s indexed by ChatGPT, Perplexity and Claude. Take 30 seconds
          to claim your listing — it&apos;s free, and unlocks:
        </>
      ),
      benefits: [
        "Property dashboard (edit photos, rates, amenities, seasonal notes)",
        "AI-generated summary + FAQs that surface when travellers ask an AI \"where to stay in your suburb\"",
        "Direct booking link + phone slot (bypass Booking.com's 15-18% fees)",
        "Weekly owner digest: how many people viewed your listing, direct-clicks, and top questions asked",
      ],
      successMessage: (formEmail) => (
        <>
          We&apos;ve flipped your listing to claimed, recorded <strong>{formEmail}</strong>{" "}
          as the owner, and kicked off AI enrichment — your page will refresh
          with an AI-generated summary and FAQs within the next 60 seconds.
        </>
      ),
      successBenefits: [
        ["📊", "Property dashboard", "manage your listing, photos, rates and seasonal notes."],
        ["🤖", "AI-enhanced page", "auto-generated summary, highlights and FAQs — live in ~60 seconds."],
        ["📈", "Weekly owner digest", "views, direct clicks and top guest questions every Monday."],
        ["📬", "Accommodation operators newsletter", "monthly industry insights, tax + rates changes."],
      ],
      newsletterLabel: "Send me the monthly Accommodation Operators newsletter",
      subscribedLabel: "subscribed you to the monthly Accommodation Operators newsletter",
      skippedLabel: "skipped the operators newsletter for now",
      metaFields: [restaurant?.stars ? `${restaurant.stars}★` : null,
                   restaurant?.sub_niche, restaurant?.suburb, restaurant?.state_code],
      dashboardCta: "Open my property dashboard →",
      dashboardHref: "/agency/hotels",
    };
  }
  // Restaurant fallback (original)
  return {
    kind: "restaurant",
    eyebrow: "Claim your restaurant",
    submitLabel: "Claim my restaurant →",
    body: (
      <>
        You were featured in this week&apos;s Local Hustle Brisbane newsletter as one of three
        restaurants where readers should try our Recipe of the Week. Take 30 seconds
        to claim your listing — it&apos;s free, and unlocks:
      </>
    ),
    benefits: [
      "Vendor dashboard (edit photos, hours, menu)",
      "Voucher + deal slots in future newsletters",
      "Weekly recipe-matched insights (\"readers want this dish on your menu\")",
      "B2B Restaurant Operators newsletter (Tuesday delivery)",
    ],
    successMessage: (formEmail) => (
      <>
        We&apos;ve flipped your listing to claimed, recorded <strong>{formEmail}</strong>{" "}
        as the owner.
      </>
    ),
    successBenefits: [
      ["📊", "Vendor dashboard", "manage your listing, photos, opening hours."],
      ["💰", "Vouchers & deals", "push offers into Local Hustle newsletters."],
      ["🍳", "Recipe Watch", "see which Recipes of the Week match your menu."],
      ["📬", "B2B newsletter", "Brisbane operator news every Tuesday."],
    ],
    newsletterLabel: "Send me the weekly Restaurant Operators newsletter",
    subscribedLabel: "subscribed you to the B2B Restaurant Operators newsletter",
    skippedLabel: "skipped the B2B newsletter for now",
    metaFields: [restaurant?.cuisine, restaurant?.suburb, restaurant?.address],
    dashboardCta: "Open my vendor dashboard →",
    dashboardHref: "https://hustleplaza.au/",
  };
}

export default function ClaimPage() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: "", restaurant: null, expires_at: null });
  const [form, setForm] = useState({ owner_email: "", owner_name: "", subscribe_b2b: true });
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);
  const [touched, setTouched] = useState({ email: false, name: false });

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/api/claim/${token}`)
      .then(({ data }) => setState({ loading: false, error: "", restaurant: data.restaurant, expires_at: data.expires_at }))
      .catch((e) => setState({ loading: false, error: e?.response?.data?.detail || "Invalid or expired link.", restaurant: null }));
  }, [token]);

  // Derived verification signals — computed live from form.owner_email +
  // the listing's public website field. Nothing here is server-side yet
  // (the /complete endpoint still records the claim), but the UI now
  // TELLS the operator when an email/domain mismatch would be worth
  // reviewing — a substantial guardrail vs the previous "any email works".
  const verify = useMemo(() => {
    const email = (form.owner_email || "").trim();
    const [, emailDomain] = splitEmail(email);
    const siteDomain = extractDomain(state.restaurant?.website);
    const emailValid = EMAIL_RE.test(email);
    const hasSite = !!siteDomain;
    const match = hasSite && emailValid && domainMatches(emailDomain, siteDomain);
    return { email, emailDomain, siteDomain, emailValid, hasSite, match };
  }, [form.owner_email, state.restaurant?.website]);

  const emailError = touched.email && form.owner_email && !verify.emailValid
    ? "That doesn't look like a valid email address."
    : "";
  const nameError = touched.name && !form.owner_name.trim()
    ? "Please enter your full name."
    : "";

  const canSubmit = verify.emailValid && form.owner_name.trim().length > 1 && !busy && !state.restaurant?.is_claimed;

  const submit = async (e) => {
    e.preventDefault();
    setTouched({ email: true, name: true });
    if (!canSubmit) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/claim/${token}/complete`, {
        ...form,
        // Send the derived domain-match hint so the backend can flag
        // mismatched claims for operator review without changing the
        // API shape.
        domain_matches_website: verify.hasSite ? verify.match : null,
      });
      setDone(data);
    } catch (e) {
      setState((s) => ({ ...s, error: e?.response?.data?.detail || "Couldn't complete claim. Please try again." }));
    } finally { setBusy(false); }
  };

  if (state.loading) return <div className="rp-wrap"><p>Validating your link…</p></div>;

  const copy = buildCopy(state.restaurant || {});

  if (state.error) return (
    <div className="rp-wrap">
      <div className="rp-card" style={{ textAlign: "center" }} data-testid="claim-error">
        <span className="rp-eyebrow">{copy.eyebrow}</span>
        <h1>Link issue</h1>
        <p>{state.error}</p>
        <Link to="/" className="rp-btn" data-testid="claim-error-back">Back home</Link>
      </div>
    </div>
  );

  if (done) return (
    <div className="rp-wrap" data-testid="claim-success">
      <div className="rp-card" style={{ textAlign: "center" }}>
        <span className="rp-eyebrow">🎉 Claimed</span>
        <h1>{state.restaurant?.name} is yours.</h1>
        <p className="rp-blurb">
          {copy.successMessage(form.owner_email)}{" "}
          {done.subscribed_b2b ? copy.subscribedLabel : copy.skippedLabel}.
        </p>
        <ul style={{ textAlign: "left", lineHeight: 1.8, marginTop: 18, listStyle: "none", padding: 0 }}>
          {copy.successBenefits.map(([icon, title, blurb]) => (
            <li key={title} style={{ marginBottom: 8 }}>
              <span style={{ marginRight: 8 }}>{icon}</span>
              <strong>{title}</strong> — {blurb}
            </li>
          ))}
        </ul>
        <a href={copy.dashboardHref} className="rp-btn" style={{ marginTop: 20 }}
            data-testid="claim-success-cta">
          {copy.dashboardCta}
        </a>
      </div>
    </div>
  );

  const r = state.restaurant || {};
  return (
    <div className="rp-wrap" data-testid="claim-page">
      <div className="rp-bar">
        <Link to="/" className="rp-back">← Local Hustle</Link>
      </div>
      <div className="rp-card">
        <span className="rp-eyebrow">{copy.eyebrow}</span>
        <h1 data-testid="claim-listing-name">{r.name}</h1>
        <p className="rp-meta">{copy.metaFields.filter(Boolean).join(" · ")}</p>
        {r.is_claimed && (
          <p className="rp-err" data-testid="claim-already-claimed">
            This listing has already been claimed.
          </p>
        )}

        <p>{copy.body}</p>
        <ul style={{ lineHeight: 1.7 }} data-testid="claim-benefits">
          {copy.benefits.map((b) => <li key={b}>{b}</li>)}
        </ul>

        <form onSubmit={submit} className="rp-form" style={{ marginTop: 22 }} data-testid="claim-form">
          <label>Your email (owner / manager)</label>
          <input type="email" required placeholder="you@example.com" value={form.owner_email}
                  onBlur={() => setTouched((t) => ({ ...t, email: true }))}
                  aria-invalid={!!emailError}
                  aria-describedby="claim-email-hint"
                  onChange={(e) => setForm({ ...form, owner_email: e.target.value })} data-testid="claim-email" />
          {emailError && (
            <p className="rp-err" style={{ marginTop: 4, fontSize: 12 }}
               data-testid="claim-email-error">{emailError}</p>
          )}
          {/* Live domain-match verification signal — only shown when the
              email is well-formed AND we have a website on the listing. */}
          {verify.emailValid && verify.hasSite && (
            verify.match ? (
              <p id="claim-email-hint"
                 style={{ margin: "6px 0 2px", fontSize: 12, color: "#2D7A4F" }}
                 data-testid="claim-email-domain-match">
                ✓ Your email matches the property&apos;s website (<code>@{verify.siteDomain}</code>).
              </p>
            ) : (
              <p id="claim-email-hint"
                 style={{ margin: "6px 0 2px", fontSize: 12, color: "#7A5F1F" }}
                 data-testid="claim-email-domain-mismatch">
                ⚠ Your email domain (<code>@{verify.emailDomain}</code>) doesn&apos;t match the
                property&apos;s website (<code>@{verify.siteDomain}</code>). You can still
                claim — we&apos;ll flag it for a quick manual review before enabling
                the dashboard.
              </p>
            )
          )}
          {verify.emailValid && !verify.hasSite && (
            <p id="claim-email-hint"
               style={{ margin: "6px 0 2px", fontSize: 12, color: "#6C7285" }}
               data-testid="claim-email-no-website">
                No public website on file — we&apos;ll verify your ownership by phone
                or through a short manual review.
            </p>
          )}
          <label style={{ marginTop: 12 }}>Your name</label>
          <input placeholder="Full name" value={form.owner_name}
                  onBlur={() => setTouched((t) => ({ ...t, name: true }))}
                  aria-invalid={!!nameError}
                  onChange={(e) => setForm({ ...form, owner_name: e.target.value })} data-testid="claim-name" />
          {nameError && (
            <p className="rp-err" style={{ marginTop: 4, fontSize: 12 }}
               data-testid="claim-name-error">{nameError}</p>
          )}
          <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12 }}>
            <input type="checkbox" checked={form.subscribe_b2b}
                    onChange={(e) => setForm({ ...form, subscribe_b2b: e.target.checked })}
                    data-testid="claim-newsletter-optin" />
            <span>{copy.newsletterLabel}</span>
          </label>
          <button type="submit" className="rp-btn" disabled={!canSubmit}
                    data-testid="claim-submit">
            {busy ? "Claiming…" : copy.submitLabel}
          </button>
        </form>
        <small style={{ display: "block", marginTop: 14, color: "#888" }}>
          Link expires {state.expires_at
              ? new Date(state.expires_at).toLocaleDateString(
                  "en-AU", { day: "numeric", month: "short", year: "numeric" })
              : "soon"}
        </small>
      </div>
    </div>
  );
}
